import React, { useRef, useEffect } from 'react';
import {
  Typography,
  CircularProgress,
  styled,
} from '@mui/material';
import { Message, Agent } from '../../stores/chatStore';
import MessageItem from './MessageItem';
import TypingIndicator from './TypingIndicator';
import ProcessingIndicator from './ProcessingIndicator';
import { format, isToday, isYesterday } from 'date-fns';

interface MessageListProps {
  messages: Message[];
  agents: Agent[];
  currentUserId: string;
  isLoading?: boolean;
  isProcessing?: boolean;
  processingAgentName?: string;
  typingAgentIds?: string[];
  highlightedMessageId?: string | null;  // NEW: For deep linking from Graph
  conversationType?: 'ai_chat' | 'dm' | 'group' | 'channel';
  onEditMessage?: (messageId: string, content: string) => void;
  onReplyMessage?: (messageId: string) => void;
  onReactMessage?: (messageId: string, emoji: string) => void;
  onRemoveReaction?: (messageId: string, emoji: string) => void;
  onOpenPanel?: (panelType: string, metadata: any) => void;
}

const MessageListContainer = styled('div')(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  minHeight: 0, // Important for proper scrolling
}));

const DateDivider = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  margin: theme.spacing(2, 0),
  '&::before, &::after': {
    content: '""',
    flex: 1,
    height: 1,
    backgroundColor: theme.palette.divider,
  },
}));

const DateLabel = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(0, 2),
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
}));

const LoadingContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(4),
}));

const MessageList: React.FC<MessageListProps> = ({
  messages,
  agents,
  currentUserId,
  isLoading = false,
  isProcessing = false,
  processingAgentName,
  typingAgentIds = [],
  highlightedMessageId = null,
  conversationType = 'ai_chat',
  onEditMessage,
  onReplyMessage,
  onReactMessage,
  onRemoveReaction,
  onOpenPanel,
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevMessageCount = useRef(messages.length);

  // Debug logging (commented out for production)
  // useEffect(() => {
  //   console.log('[MessageList] Messages received:', messages.length, 'messages');
  //   if (messages.length > 0) {
  //     console.log('[MessageList] First message ID:', messages[0].id);
  //   }
  // }, [messages]);

  // Auto-scroll to bottom when new messages arrive or on initial load
  useEffect(() => {
    // Always scroll to bottom on initial load or when new messages are added
    if (messages.length > 0 && (prevMessageCount.current === 0 || messages.length > prevMessageCount.current)) {
      setTimeout(() => {
        // Try both methods to ensure scrolling works
        if (containerRef.current) {
          containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
        if (bottomRef.current) {
          bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }, 100); // Small delay to ensure DOM is updated
    }
    
    prevMessageCount.current = messages.length;
  }, [messages]);

  // Also scroll to bottom on initial mount
  useEffect(() => {
    setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }
      if (bottomRef.current) {
        bottomRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
      }
    }, 200);
  }, []);

  const getAgent = (message: Message): Agent | undefined => {
    // Only return agent for assistant messages
    if (message.role !== 'assistant') return undefined;
    
    // First try to find agent by ID
    if (message.user_agent_id) {
      const agent = agents.find((a) => a.id === message.user_agent_id);
      if (agent) return agent;
    }
    
    // If agent not found but we have agent info from backend, create a temporary agent object
    if (message.agent_name || message.custom_name) {
      return {
        id: message.user_agent_id || 'temp-' + message.id,
        name: message.agent_name || 'Assistant',
        custom_name: message.custom_name || message.agent_name,
        avatar_url: message.avatar_url || message.agent_avatar,
        type: message.agent_type || 'assistant',
        color: message.color,
        avatar_fallback_color: message.avatar_fallback_color,
        is_active: true,
        status: 'idle' as const,
      } as Agent;
    }
    
    return undefined;
  };

  const formatDateDivider = (date: Date): string => {
    if (isToday(date)) return 'Today';
    if (isYesterday(date)) return 'Yesterday';
    return format(date, 'MMMM d, yyyy');
  };

  const shouldShowDateDivider = (message: Message, index: number): boolean => {
    if (index === 0) return true;
    const prevMessage = messages[index - 1];
    const currentDate = new Date(message.created_at);
    const prevDate = new Date(prevMessage.created_at);
    return currentDate.toDateString() !== prevDate.toDateString();
  };

  if (messages.length === 0 && !isLoading) {
    return (
      <MessageListContainer>
        <div style={{ textAlign: 'center', paddingTop: 64, paddingBottom: 64 }}>
          <Typography variant="body2" color="text.secondary">
            No messages yet. Start a conversation!
          </Typography>
        </div>
      </MessageListContainer>
    );
  }

  return (
    <MessageListContainer ref={containerRef}>
      {messages.map((message, index) => (
        <React.Fragment key={message.id}>
          {shouldShowDateDivider(message, index) && (
            <DateDivider>
              <DateLabel>{formatDateDivider(new Date(message.created_at))}</DateLabel>
            </DateDivider>
          )}
          <MessageItem
            message={message}
            agent={getAgent(message)}
            currentUserId={currentUserId}
            conversationType={conversationType}
            onEdit={onEditMessage}
            onReply={onReplyMessage}
            onReact={onReactMessage}
            onRemoveReaction={onRemoveReaction}
            onOpenPanel={onOpenPanel}
            isHighlighted={highlightedMessageId === message.id}
          />
        </React.Fragment>
      ))}
      
      {/* Processing indicator - shows while system is thinking */}
      {isProcessing && !typingAgentIds.length && (
        <ProcessingIndicator 
          agentName={processingAgentName}
        />
      )}
      
      {/* Typing indicator for agents */}
      {typingAgentIds.length > 0 && (
        <TypingIndicator 
          agents={agents} 
          typingAgentIds={typingAgentIds} 
        />
      )}
      
      {isLoading && (
        <LoadingContainer>
          <CircularProgress size={24} />
        </LoadingContainer>
      )}
      
      <div ref={bottomRef} />
    </MessageListContainer>
  );
};

export default MessageList;